import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  FaMinusCircle,
  FaPlusCircle,
  FaSortAmountDown,
  FaSort,
  FaSortAmountUpAlt,
  FaFilePdf
} from "react-icons/fa";
import {
  useTable,
  useRowSelect,
  useFilters,
  useSortBy,
  usePagination,
  useExpanded
} from "react-table";
import {
  showToast,
  formatDateWithDayjs,
  showAlert,
  sanitizeInput,
  formatToUSD
} from "../../../../utils/utils";
import { COLUMNS } from "./DemoAccColumns";
import { Link } from "react-router-dom";
import publicAxios from "../../../../utils/publicAxios";
import DualPagination from "../../../../components/common/DualPagination";
import DatePicker from "../../../datepicker/FundedDatePicker";
import PropTypes from "prop-types";
import style from "./../../../../assets/scss/custom.module.scss";
import "./fundedtable.scss";
import {
  aboutSignUpContent,
  alertActions,
  dashboard,
  fundedInvoicesData,
  invalidToastContent,
  repaymentDateAlert,
  SearchText,
  successToastContent
} from "../../../../locales/en";
import {
  getDemoQuoteUpdateRepayDateURL,
  getDemoQuoteURL
} from "../../../../utils/constants/apiEndPoints";
import { ADMIN_DEMO_ACCOUNT_INVOICES } from "../../../../route/paths";
import {
  PAGE_INDEX,
  PAGE_SIZE_25
} from "../../../../utils/constants/appConstants";

const renderSortIcon = (column) => {
  if (!column.isSorted) {
    return <FaSort />;
  }
  return column.isSortedDesc ? <FaSortAmountDown /> : <FaSortAmountUpAlt />;
};

const DemoAccFundedtable = (props) => {
  const { apiHit } = props;
  const columns = useMemo(() => COLUMNS, []);
  const [tempSearchQuery, setTempSearchQuery] = useState("");
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_25);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const userData = localStorage.getItem("fundTap_demoUser_account_setup");
  const parsedData = JSON.parse(userData);
  const userId = parsedData?.id;

  const handleFundedDataGet = useCallback(
    async (pageNumber, pageSize) => {
      try {
        if (!userData || !userId) {
          return;
        }
        setLoading(true);
        const response = await publicAxios.get(getDemoQuoteURL(userId), {
          params: {
            keyword: sanitizeInput(searchQuery) || undefined,
            page: pageNumber,
            size: pageSize,
            order: "asc"
          }
        });

        if (
          response.data.message === dashboard.dashboardTable.quotesErrorMessage
        ) {
          setTotalPages(1);
          setData([]);
        } else {
          const responseData = response.data.data || [];
          setData(responseData);
          setTotalPages(response.data.pageInfo.totalPages);
        }
      } catch (error) {
        showToast(error.response.data.errors, invalidToastContent.status);
      } finally {
        setLoading(false);
      }
    },
    [searchQuery, userId, userData]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    state,
    page,
    gotoPage,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    pageCount,
    pageOptions
  } = useTable(
    {
      columns,
      data,
      manualPagination: true,
      pageCount: totalPages,
      autoResetPage: false,
      initialState: { pageIndex: PAGE_INDEX, pageSize: PAGE_SIZE_25 }
    },
    useFilters,
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect
  );

  const { pageIndex } = state;

  useEffect(() => {
    if (apiHit) {
      handleFundedDataGet(pageIndex, pageSize);
    }
  }, [handleFundedDataGet, apiHit, pageIndex, pageSize]);

  useEffect(() => {
    handleFundedDataGet(pageIndex, pageSize);
  }, [handleFundedDataGet, pageIndex, pageSize]);

  // Reayment Date Change Function Start
  const [showNewDateButton, setShowNewDateButton] = useState(false);
  const [selectedQuoteId, setSelectedQuoteId] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);

  const handleRepayDate = async (date, rowId) => {
    const formattedDate = date ? date.toLocaleDateString("en-CA") : "";

    setSelectedQuoteId(rowId);
    setSelectedDate(formattedDate);
    setShowNewDateButton((prevState) => ({ ...prevState, [rowId]: true }));
  };

  const handleNewDateButtonClick = async () => {
    if (!selectedQuoteId || !selectedDate) return;

    const result = await showAlert(
      repaymentDateAlert.title,
      repaymentDateAlert.subtitle,
      "Are you sure you want to change the repayment date?",
      alertActions.warning,
      alertActions.Confirm,
      alertActions.Cancel
    );

    if (!result.isConfirmed) return;

    try {
      const dataToSend = {
        demoQuoteIds: [selectedQuoteId],
        repaymentDate: selectedDate
      };

      const response = await publicAxios.put(
        getDemoQuoteUpdateRepayDateURL,
        dataToSend
      );

      if (response.status === 200) {
        showToast(response.data.message, successToastContent.status);
        setShowNewDateButton(false);
        setSelectedQuoteId(null);
        setSelectedDate(null);
        // Refresh data from backend to ensure UI displays stored repay date
        // (backend may have normalized weekend date to next weekday)
        handleFundedDataGet(pageIndex, pageSize);
      }
    } catch (error) {
      showToast(error.response.data.message, invalidToastContent.status);
    }
  };

  const [expandedRows, setExpandedRows] = useState([]);

  const isRowExpanded = (rowId) => expandedRows.includes(rowId);

  const handleDownload = (pdfUrl) => {
    const anchor = document.createElement("a");
    anchor.href = pdfUrl;
    anchor.download = pdfUrl.split("/").pop();
    anchor.target = "_blank";
    anchor.click();
  };

  const toggleRow = (rowId) => {
    setExpandedRows((prevState) =>
      prevState.includes(rowId)
        ? prevState.filter((id) => id !== rowId)
        : [...prevState, rowId]
    );
  };

  // Handle changes in the search input
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setTempSearchQuery(value);
    if (value.trim() === "") {
      setSearchQuery("");
    }
  };

  // Handle key press events in the search input
  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      const trimmedQuery = tempSearchQuery.trim();
      setSearchQuery(trimmedQuery);
      gotoPage(0);
    }
  };

  if (loading) {
    return (
      <div className="spinner-wrap bg-light loader_absolute">
        <div className="spinner-border custom-spinner-color" />
      </div>
    );
  }

  const currencyFields = ["fundingAmount", "feeAmount"];
  const dateFields = ["fundedAt", "dueDate"];

  const formatValue = (accessor, value) => {
    if (currencyFields.includes(accessor)) {
      return formatToUSD(value);
    }

    if (dateFields.includes(accessor)) {
      return value ? formatDateWithDayjs(value, "DD-MMM-YYYY") : "";
    }

    if (accessor === "demoQuoteId") {
      return value ? `FT${value}` : "";
    }

    return value;
  };

  const getRepaymentSelected = (cell, row) => {
    if (selectedQuoteId === row.original.demoQuoteId && selectedDate) {
      return new Date(selectedDate);
    }
    if (cell.value) {
      return new Date(cell.value);
    }
    return new Date(row.original.dueDate);
  };

  const renderCell = (cell, row) => {
    if (cell.column.id === "notice") {
      return (
        row.original.notice && (
          <button
            className="notice-button"
            onClick={() => handleDownload(row.original.fundingNotice)}
          >
            <FaFilePdf />
          </button>
        )
      );
    }

    if (cell.column.id === "repaymentDate") {
      return (
        <>
          <DatePicker
            selected={getRepaymentSelected(cell, row)}
            originalDueDate={new Date(row.original.dueDate)}
            onChange={(date) => handleRepayDate(date, row.original.demoQuoteId)}
          />

          {showNewDateButton[row.original.demoQuoteId] && (
            <button
              className="btn btn-primary new-pay-date"
              onClick={handleNewDateButtonClick}
            >
              {fundedInvoicesData.funded.newDate}
            </button>
          )}
        </>
      );
    }

    return cell.render("Cell");
  };

  return (
    <div className="funded-data-filter-tab">
      <div className="container">
        <div className="row">
          <div className="col-12">
            <div className="col-12 sales-page-title-wrap sub-heading-blog-wrap pb-2 pe-0 ps-0 mb-3">
              <div className="row box-heading">
                <div className="col-md-4 d-flex flex-wrap align-items-center" />
                <div className="col-md-8">
                  <ul
                    className={`${style.searchOptionWrap} d-flex flex-wrap align-items-center justify-content-md-end`}
                  >
                    <li className="my-1">
                      <div className="refferal-search-wrap">
                        <div className="filtersearch mb-1 mt-1">
                          <span>
                            <input
                              type="search"
                              value={tempSearchQuery}
                              onChange={handleSearchChange}
                              placeholder={SearchText}
                              onKeyDown={handleKeyDown}
                              className="rounded-0"
                            />
                          </span>
                        </div>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <table
              className="mt-2 mb-3 th_padding_reduce demo-acc-fundedtable"
              {...getTableProps()}
            >
              <thead>
                {headerGroups.map((headerGroup) => {
                  const { key, ...rest } = headerGroup.getHeaderGroupProps();
                  return (
                    <tr className="parentrow" {...rest} key={key}>
                      <th />
                      {headerGroup.headers.map((column, index) => (
                        <th
                          {...column.getHeaderProps(
                            column.getSortByToggleProps()
                          )}
                          key={column.id}
                        >
                          {index === headerGroup.headers.length - 1 ? (
                            <span>{column.render("Header")}</span>
                          ) : (
                            <>
                              <span>{column.render("Header")}</span>
                              <span className="short-icon">
                                {renderSortIcon(column)}
                              </span>
                            </>
                          )}
                        </th>
                      ))}
                    </tr>
                  );
                })}
              </thead>
              <tbody {...getTableBodyProps()}>
                {page.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length + 2} className="text-center">
                      {fundedInvoicesData.statusMessage.msg13}
                    </td>
                  </tr>
                ) : (
                  page.map((row) => {
                    prepareRow(row);
                    const isExpanded = isRowExpanded(row.id);
                    return (
                      <React.Fragment key={row.id}>
                        <tr className="parentrow" {...row.getRowProps()}>
                          <td className="expand-td">
                            <span
                              className="expandbtn cursor-pointer"
                              onClick={() => toggleRow(row.id)}
                            >
                              {isExpanded ? (
                                <FaMinusCircle />
                              ) : (
                                <FaPlusCircle />
                              )}
                            </span>
                          </td>
                          {row.cells.map((cell) => (
                            <td
                              key={cell.getCellProps().key}
                              {...cell.getCellProps()}
                            >
                              {renderCell(cell, row)}
                            </td>
                          ))}
                        </tr>
                        {isExpanded && (
                          <tr className="childrow colexpand">
                            <td colSpan={columns.length + 2}>
                              <div className="tdbody">
                                {columns.map((column) => {
                                  const value = row.original[column.accessor];
                                  return (
                                    <span
                                      key={column.accessor}
                                      data-label={column.Header}
                                    >
                                      {formatValue(column.accessor, value)}
                                    </span>
                                  );
                                })}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
            <DualPagination
              gotoPage={gotoPage}
              previousPage={previousPage}
              nextPage={nextPage}
              canPreviousPage={canPreviousPage}
              canNextPage={canNextPage}
              pageIndex={pageIndex}
              pageCount={pageCount}
              pageOptions={pageOptions}
              setPageSize={setPageSize}
              pageSize={pageSize}
              dataLength={data.length}
            />

            {data.length === 0 && (
              <div className="demo-get-started">
                <Link to={ADMIN_DEMO_ACCOUNT_INVOICES} className="btn">
                  {aboutSignUpContent.content}
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

DemoAccFundedtable.propTypes = {
  apiHit: PropTypes.func.isRequired
};

export default DemoAccFundedtable;
